import { Editor, EditorContent, EditorStateSnapshot, useEditorState } from '@tiptap/react';
import clsx from 'clsx';
import React, { forwardRef, useEffect, useState } from 'react';
import { FaRedo, FaUndo } from 'react-icons/fa';
import { FaBold, FaItalic, FaLink, FaLinkSlash, FaListOl, FaListUl } from 'react-icons/fa6';

import { usePromptContext } from '@/components/interactions';
import { PromptFn } from '@/components/interactions/prompt-context';
import { ButtonGroup, ButtonGroupButton } from '@/components-new/button-group';
import { Field } from '@/components-new/fieldset';
import { Listbox, ListboxLabel, ListboxOption } from '@/components-new/listbox';
import { useTextEditor } from '@/components-new/rich-text-editor.hooks';

type ParagraphLevelOption = {
  label: string;
  value: 'paragraph';
}

type HeadingLevelOption = {
  label: string;
  value: 'heading';
  level: 1 | 2 | 3;
};

// option available to user for level select
const levelOptions: Array<ParagraphLevelOption | HeadingLevelOption> = [
  { label: 'Paragraph', value: 'paragraph', },
  { label: 'Heading 1', value: 'heading', level: 1 },
  { label: 'Heading 2', value: 'heading', level: 2 },
  { label: 'Heading 3', value: 'heading', level: 3, }
];

const editorStateSelector = ({ editor }: EditorStateSnapshot<Editor>) => ({
  isBoldActive: editor.isActive('bold'),
  isItalicActive: editor.isActive('italic'),
  isFocused: editor.isFocused,
  isParagraphActive: editor.isActive('paragraph'),
  isHeadingActive: (level: 1 | 2 | 3) => editor.isActive('heading', { level }),
  isBulletListActive: editor.isActive('bulletList'),
  isOrderedListActive: editor.isActive('orderedList'),
  isLinkActive: editor.isActive('link'),
});

const isFocusedAndActive = (editorState: EditorState, condition: boolean) => editorState.isFocused && condition;

type EditorState = ReturnType<typeof editorStateSelector>;

/**
 * Toolbar control that allows user to select what level of text they would like to use when typing in the editor.
 */
const LevelSelect = ({ editor, editorState }: { editor: Editor; editorState: EditorState; }) => {
  const [selectedLevel, setSelectedLevel] = useState(levelOptions[0].label);

  const handleLevelItemSelect = (value: string) => {
    const foundOption = levelOptions.find((levelOption) => levelOption.label === value);
    if (foundOption?.value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else if (foundOption?.value === 'heading') {
      editor.chain().focus().setHeading({ level: foundOption.level }).run();
    }
  };

  useEffect(() => {
    const handleUpdate = () => {
      setSelectedLevel(_ => {
        const selectedOption = levelOptions.find((option) => option.value === 'paragraph' && editorState.isParagraphActive || option.value === 'heading' && editorState.isHeadingActive((option as HeadingLevelOption)?.level));

        return selectedOption ? selectedOption.label : levelOptions[0].label;
      });
    };

    if (editor) {
      editor.on('update', handleUpdate);
      editor.on('selectionUpdate', handleUpdate);
    }

    return () => {
      if (editor) {
        editor.off('update', handleUpdate);
        editor.off('selectionUpdate', handleUpdate);
      }
    };
  }, [editor, editorState]);

  return (
    <Field>
      <Listbox
        value={selectedLevel}
        onChange={handleLevelItemSelect}>
        {levelOptions.map((option) => (
          <ListboxOption key={option.label} value={option.label}>
            <ListboxLabel>{option.label}</ListboxLabel>
          </ListboxOption>
        ))}
      </Listbox>
    </Field>
  );
};

/**
 * Toolbar control that allows user to select what inline text-mark they would like to use.
 */
const TextMarkControls = ({ editor, editorState }: { editor: Editor; editorState: EditorState }) => {
  return (
    <ButtonGroup>
      <ButtonGroupButton
        title="Bold"
        aria-label="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        solid={isFocusedAndActive(editorState, editorState.isBoldActive)}
      >
        <FaBold/>
      </ButtonGroupButton>
      <ButtonGroupButton
        title="Italic"
        aria-label="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        solid={isFocusedAndActive(editorState, editorState.isItalicActive)}
      >
        <FaItalic/>
      </ButtonGroupButton>
    </ButtonGroup>
  );
};

/**
 * Toolbar control that allows the users to select the list formatting the would like to use.
 */
const ListControls = ({ editor, editorState }: { editor: Editor; editorState: EditorState }) => {
  return (
    <ButtonGroup>
      <ButtonGroupButton
        title="Bullet list"
        aria-label="Bullet list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={!editor.can().chain().focus().toggleBulletList().run()}
        solid={isFocusedAndActive(editorState, editorState.isBulletListActive)}
      >
        <FaListUl/>
      </ButtonGroupButton>
      <ButtonGroupButton
        title="Numbered list"
        aria-label="Numbered list"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={!editor.can().chain().focus().toggleOrderedList().run()}
        solid={isFocusedAndActive(editorState, editorState.isOrderedListActive)}
      >
        <FaListOl/>
      </ButtonGroupButton>
    </ButtonGroup>
  );
};

/**
 * Toolbar control that allows users to set and unset external links.
 */
const LinkControls = ({ editor, editorState }: { editor: Editor; editorState: EditorState }) => {
  const { prompt } = usePromptContext();

  const handleSetLink = (editor: Editor, prompt: PromptFn) =>  {
    const existingHref = isFocusedAndActive(editorState, editorState.isLinkActive) ? editor.getAttributes('link').href : '';
    prompt({ title: 'Add link', text: 'Enter the URL for this link.', defaultValue: existingHref })
      .then((value: string) => {
        const trimmedValue = value.trim();

        if (trimmedValue) {
          editor.chain().focus().setLink({ href: trimmedValue }).run();
        } else if (isFocusedAndActive(editorState, editorState.isLinkActive)) {
          editor.chain().focus().unsetLink().run();
        }
      });
  };

  return (
    <ButtonGroup>
      <ButtonGroupButton
        title="Set link"
        aria-label="Set link"
        onClick={() => handleSetLink(editor, prompt)}
      >
        <FaLink/>
      </ButtonGroupButton>
      <ButtonGroupButton
        title="Unset link"
        aria-label="Unset link"
        onClick={() => editor.chain().focus().unsetLink().run()}
      >
        <FaLinkSlash/>
      </ButtonGroupButton>
    </ButtonGroup>
  );
};

/**
 * Toolbar control that allows user to navigate their edit history.
 */
const HistoryControls = ({ editor }: { editor: Editor }) => {
  return (
    <ButtonGroup>
      <ButtonGroupButton
        title="Undo"
        aria-label="Undo"
        onClick={() => editor.chain().undo().run()}
        disabled={!editor.can().chain().undo().run()}
      >
        <FaUndo/>
      </ButtonGroupButton>
      <ButtonGroupButton
        title="Redo"
        aria-label="Redo"
        onClick={() => editor.chain().redo().run()}
        disabled={!editor.can().chain().redo().run()}
      >
        <FaRedo/>
      </ButtonGroupButton>
    </ButtonGroup>
  );
};

/**
 * Toolbar used for managing the way a user wants text in the editor to look when they type it.
 */
export const RichTextEditorToolbar = ({
  editor,
  className,
}: { editor: Editor, className?: string }) => {
  const editorState = useEditorState({
    editor,
    selector: editorStateSelector,
  });
  return (
    <div className={clsx(className, 'w-full')}>
      <div className="flex w-full items-center gap-4">
        <LevelSelect editor={editor} editorState={editorState}/>
        <TextMarkControls editor={editor} editorState={editorState}/>
        <ListControls editor={editor} editorState={editorState}/>
        <LinkControls editor={editor} editorState={editorState}/>
        <HistoryControls editor={editor}/>
      </div>
    </div>
  );
};

/**
 * Readonly preview of rich-text.
 */
export const RichTextPreview = ({ content, className }: { content: string, className?: string }) => {
  const { editor } = useTextEditor({ value: content, editable: false });

  if (!editor) return null;

  return (
    <RichTextEditorContent editor={editor} className={className}/>
  );
};

/**
 * Controllable rich text content editor.
 */
export const RichTextEditorContent = forwardRef((
  { editor, className, ...props }: { editor: Editor } & React.ComponentPropsWithoutRef<'div'>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  return (
    <EditorContent
      {...props}
      editor={editor}
      ref={ref}
      className={clsx(className, 'prose h-full overflow-auto bg-white antialiased')}
    />
  );
});

type RichTextEditorInputProps = {
  className?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (event: string) => void;
  placeholder?: string;
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'>

/**
 * Write a lot of gorgeous text, you know, like a blog post 😉.
 */
export const RichTextEditorInput = forwardRef((
  {
    className,
    onChange,
    disabled,
    value,
    placeholder,
    ...props
  }: RichTextEditorInputProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const { editor } = useTextEditor({
    editable: !disabled,
    onChange,
    value,
    placeholder,
  });

  if (!editor) return null;

  return (
    <div
      data-slot="control"
      className={clsx([
        className,
        'flex w-full flex-col overflow-hidden',
        'relative' // Add relative positioning for the overlay
      ])}
    >
      {!disabled && <RichTextEditorToolbar editor={editor} className="mb-2"/> }
      {disabled && (
        <div className="mb-2 flex justify-end">
          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
            Read-only
          </span>
        </div>
      )}
      <RichTextEditorContent
        editor={editor}
        className={clsx([
          // Basic layout
          'relative flex size-full appearance-none flex-col rounded-lg px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
          // Typography
          'text-base/6 text-zinc-950 placeholder:text-zinc-500 dark:text-white sm:text-sm/6',
          // Border
          'border border-zinc-950/10 data-[hover]:border-zinc-950/20 dark:border-white/10 dark:data-[hover]:border-white/20',
          // Background color - Change bg when disabled
          disabled ? 'bg-gray-50' : 'bg-transparent dark:bg-white/5',
          // focus
          'focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500',
          // Invalid state
          'data-[invalid]:border-red-500 data-[invalid]:data-[hover]:border-red-500 data-[invalid]:dark:border-red-600 data-[invalid]:data-[hover]:dark:border-red-600',
          // Disabled state
          'disabled:border-zinc-950/20 disabled:dark:border-white/15 disabled:dark:bg-white/[2.5%] dark:data-[hover]:disabled:border-white/15',
          // Add text color change when disabled
          disabled ? 'text-gray-500' : '',
          'prose flex-1 overflow-auto'
        ])}
        {...props}
        ref={ref}
      />
      {disabled && (
        <div className="pointer-events-none absolute inset-0 bg-gray-100/5"/>
      )}
    </div>
  );
});